'use client';

import { useState, useEffect, useCallback } from 'react';
import { GameComment } from '@/types';
import { LanguageCode } from '@/lib/games';
import { translations } from '@/lib/translations';

interface CommentsProps {
  gameId: string;
  lang: LanguageCode;
}

const Comments = ({ gameId, lang }: CommentsProps) => {
  const [comments, setComments] = useState<GameComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const fetchComments = useCallback(async () => {
    try {
      const response = await fetch(`/api/comments?gameId=${gameId}&sort=${sortBy}`);
      if (!response.ok) throw new Error('Failed to fetch comments');
      const data = await response.json();
      setComments(data.comments || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Unable to load comments');
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [gameId, sortBy]);

  useEffect(() => {
    fetchComments();
  }, [gameId, sortBy, fetchComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          gameId,
          name,
          content,
        }),
      });

      if (!response.ok) throw new Error('Failed to post comment');
      
      await fetchComments();
      setName('');
      setContent('');
    } catch (err) {
      console.error('Error posting comment:', err);
      setError('Failed to post comment');
    }
  };

  if (isLoading) {
    return <div className="animate-pulse">Loading comments...</div>;
  }

  return (
    <section className="comments-section bg-white/80 rounded-lg p-6">
      <div className="comments-header flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">{translations.comments.title[lang]}</h2>
          <p className="text-gray-500">({comments.length})</p>
        </div>
        <div className="sort-by">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="newest">{translations.comments.sortBy.newest[lang]}</option>
            <option value="oldest">{translations.comments.sortBy.oldest[lang]}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <div className="space-y-4 mt-8">
        {Array.isArray(comments) && comments.map((comment) => (
          <div key={comment.id} className="bg-white/80 p-4 rounded-lg shadow">
            <div className="font-semibold text-gray-800">{comment.name}</div>
            <div className="text-gray-600 mt-2">{comment.content}</div>
            <div className="text-sm text-gray-400 mt-2">
              {new Date(comment.createdAt).toLocaleDateString(lang)}
            </div>
          </div>
        ))}
        {!error && (!comments || comments.length === 0) && (
          <p className="text-gray-500">{translations.comments.noComments[lang]}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-6">
        <div className="grid grid-cols-1 gap-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={translations.comments.placeholders.name[lang]}
            required
            className="w-full px-4 py-2 border rounded"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder={translations.comments.placeholders.content[lang]}
            required
            className="w-full px-4 py-2 border rounded"
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            {translations.comments.submitButton[lang]}
          </button>
        </div>
      </form>
    </section>
  );
};

export default Comments;